<template>
  <!--
    Custom modal (not the shared Modal molecule) so the PopupCard can render edge-to-edge,
    keeping the image full-bleed on the left. The X close button is absolutely positioned
    over the card.
  -->
  <!--
    z-999 (custom Tailwind value) so the overlay covers the sticky basket bar
    (MerchantBasket uses z-500). See tailwind.config.js zIndex.
  -->
  <div v-if="open" class="fixed inset-0 z-999 overflow-y-auto" role="dialog" aria-modal="true">
    <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" @click="dismiss"></div>
    <div class="flex min-h-full items-center justify-center md:p-4">
      <div
        class="relative w-full md:max-w-4xl transform overflow-hidden bg-white shadow-xl transition-all flex flex-col min-h-screen md:min-h-0 md:max-h-[90vh] md:rounded-lg"
      >
        <button
          class="absolute right-3 top-3 z-10 rounded-full bg-white/70 p-1 text-gray-500 hover:bg-white hover:text-gray-700 md:bg-transparent"
          aria-label="Close"
          @click="dismiss"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <PopupCard
          v-if="popup"
          :headline="popup.headline"
          :text="popup.body"
          :imageUrl="popup.image_url"
          :collectFromCustomer="popup.collect_from_customer"
          :audience="popup.audience"
          :submitted="submitted"
          :reward="reward"
          :error="error"
          :storeName="currentStore ? currentStore.attributes.store_name : ''"
          :storeTagline="currentStore ? currentStore.attributes.store_description : ''"
          @submit="onSubmit"
          @dismiss="dismiss"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PopupCard from '@/components/Merchant/PopupCard'
import merchantFetch from '@/utils/merchantFetch'
import { createScrollLock } from '@/utils/bodyScrollLock'

export default {
  name: 'MarketingPopup',
  components: { PopupCard },
  emits: ['bannerReceived'],

  data() {
    return {
      open: false,
      submitted: false,
      reward: null,
      error: '',
      popup: null,
      requested: false
    }
  },

  computed: {
    currentStore() {
      const bottle = this.$store.getters.currentBottle
      if (!bottle) return null
      return this.$store.getters.store(bottle.relationships?.store?.data)
    }
  },

  watch: {
    '$route.name'() {
      this.maybeOpen()
    },
    currentStore(val) {
      if (val) this.maybeOpen()
    },
    open(val) {
      // Lock background scroll while the popup is open, matching the shared Modal behaviour.
      if (typeof document === 'undefined') return
      if (val) {
        this.scrollLock.lock()
      } else {
        this.scrollLock.unlock()
      }
    }
  },

  created() {
    this.scrollLock = createScrollLock()
  },

  mounted() {
    this.maybeOpen()
  },

  beforeUnmount() {
    if (typeof document !== 'undefined') this.scrollLock.unlock()
  },

  methods: {
    isNotSuppressed() {
      if (typeof localStorage === 'undefined') return true
      const expiry = localStorage.getItem('popup_expiry')
      if (!expiry) return true
      return new Date(expiry) <= new Date()
    },

    async maybeOpen() {
      if (this.requested) return
      if (this.$route.name !== 'Selection') return
      if (!this.currentStore) return

      // Always fetch on Selection so the banner can emit independently of popup suppression.
      // The popup's own suppression key (popup_expiry) only gates whether the popup modal opens.
      // The JWT minted via subdomain does not always carry store_id, so we pass it explicitly.
      this.requested = true
      const res = await merchantFetch('announcement_views', {
        method: 'POST',
        body: JSON.stringify({ store_id: this.currentStore.id })
      })
      if (res && res.popup && this.isNotSuppressed()) {
        this.popup = res.popup
        this.open = true
      }
      if (res && res.banner) {
        this.$emit('bannerReceived', res.banner)
      }
    },

    suppressFor7Days() {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('popup_expiry', new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString())
      }
    },

    dismiss() {
      this.suppressFor7Days()
      this.open = false
    },

    async onSubmit(payload) {
      this.error = ''

      if (this.popup.collect_from_customer === 'none') {
        this.dismiss()
        return
      }

      let body
      if (this.popup.collect_from_customer === 'email_address') {
        if (!payload.email || !payload.email.trim()) {
          this.error = 'Please enter your email address.'
          return
        }
        body = { popup_id: this.popup.id, popup_submission: { email: payload.email.trim() } }
      } else if (this.popup.collect_from_customer === 'contact_number') {
        if (!payload.phone || !payload.phone.trim()) {
          this.error = 'Please enter your phone number.'
          return
        }
        body = {
          popup_id: this.popup.id,
          popup_submission: { phone: payload.phone.trim(), country_code: payload.countryCode }
        }
      }

      try {
        const response = await merchantFetch('popup_submissions', {
          method: 'POST',
          body: JSON.stringify(body)
        })
        this.suppressFor7Days()
        this.reward = response.reward || null
        this.submitted = true
      } catch (e) {
        this.error = 'Something went wrong. Please try again.'
      }
    }
  }
}
</script>
